if (!customElements.get('customer-photo-gallery')) {
  customElements.define('customer-photo-gallery', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.scroller = this.querySelector('[data-photo-scroller]');
      this.photos = [...this.querySelectorAll('.customer-photo-gallery__photo')];
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-photo-body]');
      if (!this.scroller || !this.dialog?.showModal) return;
      this.setAttribute('data-enhanced', '');
      this.photos.forEach(photo => {
        const trigger = photo.querySelector('summary');
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
      });
      this.addEventListener('click', event => {
        const trigger = event.target.closest('summary');
        if (trigger?.closest('customer-photo-gallery') === this) {
          event.preventDefault();
          this.openPhoto(this.photos.indexOf(trigger.parentElement), trigger);
        }
        if (event.target.closest('[data-photo-prev]')) this.move(-1);
        if (event.target.closest('[data-photo-next]')) this.move(1);
        if (event.target.closest('[data-photo-close]')) this.closePhoto();
        if (event.target.closest('[data-photo-prev-look]')) this.openPhoto(this.activeIndex - 1, null, '[data-photo-prev-look]');
        if (event.target.closest('[data-photo-next-look]')) this.openPhoto(this.activeIndex + 1, null, '[data-photo-next-look]');
      }, options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.closePhoto(); }, options);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.restorePhoto(); }, options);
      const outside = event => {
        const rect = this.dialog.getBoundingClientRect();
        return event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
      };
      this.dialog.addEventListener('pointerdown', event => { this.backdropPressed = outside(event); }, options);
      this.dialog.addEventListener('click', event => {
        if (this.backdropPressed && outside(event)) this.closePhoto();
        this.backdropPressed = false;
      }, options);
      this.dialog.addEventListener('pointerdown', event => {
        this.swipe = event.isPrimary && event.pointerType === 'touch' && matchMedia('(max-width: 740px)').matches && event.target.closest('.customer-photo-gallery__panel-image')
          ? { id: event.pointerId, x: event.clientX, y: event.clientY } : null;
      }, options);
      this.dialog.addEventListener('pointerup', event => {
        const start = this.swipe;
        this.swipe = null;
        if (!start || start.id !== event.pointerId || this.photos.length < 2) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        const direction = (dx < 0 ? 1 : -1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1);
        this.openPhoto(this.activeIndex + direction);
      }, options);
      this.dialog.addEventListener('pointercancel', () => { this.swipe = null; }, options);
      this.scroller.addEventListener('scroll', () => this.schedule(), { ...options, passive: true });
      this.resizeObserver?.disconnect();
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(this.scroller);
      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', event => {
          const index = this.photos.findIndex(photo => photo.dataset.blockId === event.detail?.blockId || photo === event.target.closest('.customer-photo-gallery__photo'));
          if (index >= 0) this.openPhoto(index, this.photos[index].querySelector('summary'));
        }, options);
        this.addEventListener('shopify:block:deselect', () => this.closePhoto(), options);
      }
      this.schedule();
    }
    disconnectedCallback() {
      this.closePhoto();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.removeAttribute('data-enhanced');
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.updateScroller(); });
    }
    updateScroller() {
      const max = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
      const position = Math.min(max, Math.abs(this.scroller.scrollLeft));
      this.querySelector('[data-photo-prev]').disabled = position <= 1;
      this.querySelector('[data-photo-next]').disabled = position >= max - 1;
      const progress = this.querySelector('[data-photo-progress]');
      progress.hidden = max <= 1;
      progress.style.setProperty('--photos-progress', `${(position + this.scroller.clientWidth) / this.scroller.scrollWidth * 100}%`);
    }
    move(direction) {
      const gap = parseFloat(getComputedStyle(this.scroller).columnGap) || 0;
      const step = this.photos[0].getBoundingClientRect().width + gap;
      const count = Math.max(1, Math.floor((this.scroller.clientWidth + gap + 1) / step));
      const max = Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
      const position = Math.min(max, Math.abs(this.scroller.scrollLeft));
      const group = step * count;
      const page = direction > 0 ? Math.floor(position / group + .001) + 1 : Math.ceil(position / group - .001) - 1;
      const target = Math.max(0, Math.min(max, page * group));
      this.scroller.scrollTo({ left: target * (getComputedStyle(this).direction === 'rtl' ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    openPhoto(index, trigger, navigation) {
      if (!this.photos.length) return;
      index = (index % this.photos.length + this.photos.length) % this.photos.length;
      this.photos.forEach(photo => { photo.open = false; photo.querySelector('summary').setAttribute('aria-expanded', 'false'); });
      this.activeIndex = index;
      if (trigger) this.returnTrigger = trigger;
      this.body.replaceChildren(this.photos[index].querySelector('template').content.cloneNode(true));
      this.photos[index].querySelector('summary').setAttribute('aria-expanded', 'true');
      if (!this.dialog.open) this.dialog.showModal();
      this.dialog.scrollTop = 0;
      const focusTarget = navigation && (this.body.querySelector(navigation) || this.body.querySelector('.customer-photo-gallery__navigation'));
      (focusTarget || this.querySelector('[data-photo-close]')).focus({ preventScroll: true });
    }
    restorePhoto() {
      this.photos?.forEach(photo => photo.querySelector('summary').setAttribute('aria-expanded', 'false'));
      if (this.returnTrigger?.isConnected) this.returnTrigger.focus({ preventScroll: true });
      this.returnTrigger = null;
      this.body?.replaceChildren();
    }
    closePhoto() {
      this.swipe = null;
      this.dialog?.close();
      this.restorePhoto();
    }
  });
}
